import type { BarSegment, GroupRowModel, PenTag, TrackBadge, TrackMarker } from '../types';

/**
 * Labels are placed by percentage, so whether two collide depends on the track's width. The
 * track fills the row and is never narrower than 47rem (the board's minimum width less the
 * label column), so laying labels out at that width keeps them apart anywhere; the board lays
 * them out again at the width it measures (`layoutTrackLabels`), so a label hidden on a narrow
 * track shows once there is room.
 */
export const TRACK_MIN_WIDTH_PX = 47 * 16;
/** Room one marker label takes; "First measurement" in 9px capitals is the longest. */
const MARKER_LABEL_PX = 100;
/** A pen tag: its pen glyph, 12px bold characters, padding, and the margin after a move badge. */
const PEN_TAG_CHAR_PX = 7;
const PEN_TAG_PADDING_PX = 12;
const PEN_TAG_ICON_PX = 16;
const PEN_TAG_AFTER_MOVE_PX = 24;
/**
 * The current-pen chip at its bar's end: the tag's glyph, text and padding, a border, its
 * inset, and a gap so a tag doesn't sit flush against it repeating the same pen.
 */
const PEN_CHIP_EXTRA_PX = 2 + 4 + 8;
/** A badge label per kind in 10px bold capitals ("DELOUSING", "DESPIOJE"), and the " + ". */
const BADGE_LABEL_KIND_PX = 64;
const BADGE_LABEL_SEPARATOR_PX = 20;
/** Clear space kept between two badge labels. */
const BADGE_LABEL_GAP_PX = 8;
/**
 * A current stay that fits in the visible track shows its pen twice at once, the tag at its
 * start and the chip at its end; its tag goes, and the bar names the pen instead. This is the
 * view's width until the board has measured it.
 */
const DEFAULT_VIEW_PX = 320;

function pxToPct(px: number, trackPx: number): number {
    return (px / trackPx) * 100;
}

/**
 * Markers whose labels would overlap share one label on the first of them ("Stocking + First
 * measurement"); every marker keeps its line.
 */
export function mergeMarkerLabels(
    markers: TrackMarker[],
    trackPx: number = TRACK_MIN_WIDTH_PX
): TrackMarker[] {
    const sorted = [...markers].sort((a, b) => a.leftPct - b.leftPct);
    let leader: TrackMarker | null = null;
    return sorted.map((marker) => {
        const reach = leader ? pxToPct(MARKER_LABEL_PX * leader.labelKinds.length, trackPx) : 0;
        if (leader && marker.leftPct - leader.leftPct < reach) {
            if (!leader.labelKinds.includes(marker.kind)) leader.labelKinds.push(marker.kind);
            return { ...marker, labelKinds: [] };
        }
        leader = { ...marker, labelKinds: [marker.kind] };
        return leader;
    });
}

function penLabelPx(penName: string): number {
    return penName.length * PEN_TAG_CHAR_PX + PEN_TAG_PADDING_PX + PEN_TAG_ICON_PX;
}

/**
 * Hides each pen tag that would start inside the one before it, or that wouldn't fit inside
 * its own stay's bars (`stayEnds`, by tag key: a short stay zoomed out read "r 03"). A tag
 * for the current pen that would run into the current-pen chip at its bar's end (`endPct`), or
 * whose stay is short enough that tag and chip show together, is hidden too: the chip already
 * names that pen. A hidden tag's stay names the pen on its first bar instead.
 */
export function hideOverlappingPenTags(
    tags: PenTag[],
    currentPen: string | null = null,
    trackPx: number = TRACK_MIN_WIDTH_PX,
    endPct = 100,
    viewPx: number = DEFAULT_VIEW_PX,
    stayEnds: ReadonlyMap<string, number> = new Map()
): PenTag[] {
    const chipStart =
        currentPen === null
            ? Number.POSITIVE_INFINITY
            : endPct - pxToPct(penLabelPx(currentPen) + PEN_CHIP_EXTRA_PX, trackPx);
    let shownUntil = Number.NEGATIVE_INFINITY;
    return tags.map((tag, index) => {
        if (tag.leftPct < shownUntil) return { ...tag, hidden: true };
        const width = penLabelPx(tag.penName) + (tag.afterMove ? PEN_TAG_AFTER_MOVE_PX : 0);
        const end = tag.leftPct + pxToPct(width, trackPx);
        if (end > (stayEnds.get(tag.key) ?? Number.POSITIVE_INFINITY)) {
            return { ...tag, hidden: true };
        }
        if (tag.penName === currentPen && end > chipStart) return { ...tag, hidden: true };
        const shortCurrentStay =
            currentPen !== null &&
            index === tags.length - 1 &&
            tag.penName === currentPen &&
            endPct - tag.leftPct < pxToPct(viewPx, trackPx);
        if (shortCurrentStay) return { ...tag, hidden: true };
        shownUntil = end;
        return { ...tag, hidden: false };
    });
}

/** "REDISTRIBUTION" (es "REDISTRIBUCIÓN") is half as long again as the other kinds. */
const LONG_BADGE_LABEL_KIND_PX = 96;

function badgeLabelPx(badge: TrackBadge): number {
    const kinds = badge.kinds.reduce(
        (sum, kind) =>
            sum + (kind === 'redistribution' ? LONG_BADGE_LABEL_KIND_PX : BADGE_LABEL_KIND_PX),
        0
    );
    return kinds + (badge.kinds.length - 1) * BADGE_LABEL_SEPARATOR_PX;
}

/**
 * Hides the label of each badge whose label, centred over its disc, would run into the last
 * one shown; the disc stays and still names its events.
 */
export function hideOverlappingBadgeLabels(
    badges: TrackBadge[],
    trackPx: number = TRACK_MIN_WIDTH_PX
): TrackBadge[] {
    let shownUntil = Number.NEGATIVE_INFINITY;
    return [...badges]
        .sort((a, b) => a.leftPct - b.leftPct)
        .map((badge) => {
            const half = pxToPct(badgeLabelPx(badge) / 2, trackPx);
            if (badge.leftPct - half < shownUntil) return { ...badge, labelShown: false };
            shownUntil = badge.leftPct + half + pxToPct(BADGE_LABEL_GAP_PX, trackPx);
            return { ...badge, labelShown: true };
        });
}

type TrackLabels = Pick<
    GroupRowModel,
    'penTags' | 'markers' | 'badges' | 'segments' | 'currentPen' | 'endPct'
>;

/**
 * A row's labels laid out for a track `trackPx` wide: which pen tags show, which marker labels
 * share one, which badge labels show, and which bars name a pen whose tag is hidden (the first
 * bar of that stay). `compact` (a zoomed-out long cycle) drops every badge and marker label:
 * the discs and lines stay, named by their tooltips. `viewPx` is the visible track's width.
 */
export function layoutTrackLabels<T extends TrackLabels>(
    row: T,
    trackPx: number,
    compact = false,
    viewPx: number = DEFAULT_VIEW_PX
): T {
    const stayEnds = new Map<string, number>();
    for (const segment of row.segments) {
        const end = segment.leftPct + segment.widthPct;
        stayEnds.set(segment.stayKey, Math.max(stayEnds.get(segment.stayKey) ?? end, end));
    }
    const penTags = hideOverlappingPenTags(
        row.penTags,
        row.currentPen,
        trackPx,
        row.endPct,
        viewPx,
        stayEnds
    );
    const hidden = new Set(penTags.filter((tag) => tag.hidden).map((tag) => tag.key));
    const seen = new Set<string>();
    const segments = row.segments.map((segment): BarSegment => {
        const first = !seen.has(segment.stayKey);
        seen.add(segment.stayKey);
        return { ...segment, revealsPen: first && hidden.has(segment.stayKey) };
    });
    const markers = compact
        ? row.markers.map((marker) => ({ ...marker, labelKinds: [] }))
        : mergeMarkerLabels(row.markers, trackPx);
    const badges = compact
        ? row.badges.map((badge) => ({ ...badge, labelShown: false }))
        : hideOverlappingBadgeLabels(row.badges, trackPx);
    return { ...row, penTags, segments, markers, badges };
}
